import React, { useContext, useEffect } from 'react'
import {
  Button,
  Cell,
  DataBlock,
  DialogOverlay,
  DialogOverlayActions,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  Grid,
  IconButton,
  InlineButton,
  InputGroup,
  Row,
  TextArea,
  TextField,
  H5,
} from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { ContentAdd, ContentRemove } from '@fs/zion-icon'
import { RawData } from '../../../../common/RawData'
import { useCreateJob } from './useCreateJob'
import { jobsRequiringRole } from '../useDatsJobs'
import { PermissionsContext } from '../../../../app/PermissionsContext'

const contentSize = css`
  width: 1000px;
`

export default function CreateJob({ refreshJobs }) {
  const { t } = useTranslation()
  const { createJobState, createJobActions } = useCreateJob(refreshJobs)
  useEffect(() => {
    createJobActions.fetchTools()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <>
      <Button emphasis="medium" onClick={() => createJobActions.openDialog()}>
        {t('dats.createJob', 'Create Job')}
      </Button>
      <DialogOverlay {...createJobActions.dialogOverlay} closeOnClickAway={false} size="xxl">
        <div css={contentSize}>
          <Grid>
            {/* Row */}
            <Cell columns={12}>
              <H5 centered>{t('dats.createJob', 'Create Job')}</H5>
            </Cell>

            {/* Row */}
            <Cell columns={4}>
              <ToolsOptions createJobState={createJobState} createJobActions={createJobActions} />
            </Cell>
            <Cell columns={8}>
              <ToolSummary createJobState={createJobState} createJobActions={createJobActions} />
            </Cell>

            {/* Row */}
            <Cell columns={12}>
              <RawData state={createJobState} />
            </Cell>
          </Grid>
        </div>

        <DialogOverlayActions>
          <InputGroup>
            <Row>
              <Button
                emphasis="high"
                onClick={() => {
                  createJobActions.uploadData()
                }}
              >
                {t('action.submit', 'Submit')}
              </Button>
              <Button data-testid="cancelButton" emphasis="medium" onClick={() => createJobActions.close()}>
                {t('action.cancel', 'Cancel')}
              </Button>
            </Row>
          </InputGroup>
        </DialogOverlayActions>
      </DialogOverlay>
    </>
  )
}

function ToolsOptions({ createJobState, createJobActions }) {
  const { t } = useTranslation()
  const { isRole } = useContext(PermissionsContext)

  if (!createJobState.tools) {
    return null
  }

  return (
    <>
      {Object.keys(createJobState.tools).map((groupName) => {
        let groupTools = createJobState.tools[groupName]
        const groupHasToolsThatRequireARole = jobsRequiringRole.some(
          (jobRequiringRole) => jobRequiringRole.groupName === groupName
        )
        if (groupHasToolsThatRequireARole) {
          groupTools = groupTools.filter((tool) => {
            const jobRequiringRoles = jobsRequiringRole.find(
              (jobRequiringRole) => jobRequiringRole.jobName === tool.name
            )
            return !(jobRequiringRoles && !isRole(jobRequiringRoles.roles))
          })
        }
        return (
          <Expander key={groupName}>
            <ExpanderHeader>{t(`dats.toolGroupName.${groupName}`, `${groupName}`)}</ExpanderHeader>
            <ExpanderContent>
              {groupTools.map((tool) => {
                return (
                  <React.Fragment key={tool.name}>
                    <InlineButton onClick={() => createJobActions.setSelectedTool(tool)}>
                      {t(`dats.toolName.${tool.name}`, `${tool.name}`)}
                    </InlineButton>
                    <br />
                  </React.Fragment>
                )
              })}
            </ExpanderContent>
          </Expander>
        )
      })}
    </>
  )
}

const textFiledSize = css`
  width: 200px;
`

function ToolSummary({ createJobState, createJobActions }) {
  const { t } = useTranslation()

  if (!createJobState.selectedTool) {
    return null
  }

  return (
    <div>
      <H5>{t(`dats.toolName.${createJobState.selectedTool.name}`, `${createJobState.selectedTool.name}`)}</H5>
      <DataBlock
        data={t(
          `dats.toolDescription.${createJobState.selectedTool.description}`,
          `${createJobState.selectedTool.description}`
        )}
        heading=""
      />
      <H5>{t(`dats.parameters`, `Parameters`)}</H5>
      {createJobState.selectedTool.parameterSpecifications?.map((parameter) => {
        return (
          <DataBlock
            key={parameter.name}
            heading={t(`dats.parameterName.${parameter.name}`, `${parameter.name}`)}
            data={t(`dats.parameterDescription.${parameter.description}`, `${parameter.description}`)}
          />
        )
      })}
      <br />
      <div css={textFiledSize}>
        <TextField
          label={t(`dats.requestsPerSecond`, `Requests Per Second`)}
          value={createJobState.inputs.initialRequestsPerSecond}
          onChange={(event) => createJobActions.setInitialRequestsPerSecond(event.target.value)}
        />
      </div>
      <div css={textFiledSize}>
        <TextField
          label={t(`dats.itemsPerRequest`, 'Items Per Request')}
          value={createJobState.inputs.initialItemsPerRequest}
          onChange={(event) => createJobActions.setInitialItemsPerRequest(event.target.value)}
        />
      </div>
      {createJobState.inputs.initialParameters.list.map((item, index) => (
        <InputGroup key={item.id} label={t('dats.jobParameter', 'Job Parameter')}>
          <Row>
            <TextField
              name="parameter"
              placeholder={t('dats.Name', 'Name')}
              value={item.key}
              onChange={(event) => createJobActions.setInitialParameterKey(index, event.target.value)}
            />
            <TextField
              name="setting"
              placeholder={t('dats.Value', 'Value')}
              value={item.value}
              onChange={(event) => createJobActions.setInitialParameterValue(index, event.target.value)}
            />
            <IconButton
              Icon={ContentAdd}
              color="gray"
              title={t('dats.addParameter', 'Add Parameter')}
              onClick={() => createJobActions.addInitialParameter(index)}
            />
            {createJobState.inputs.initialParameters.list.length > 1 && (
              <IconButton
                Icon={ContentRemove}
                color="gray"
                title={t('dats.removeParameter', 'Remove Parameter')}
                onClick={() => createJobActions.removeInitialParameter(index)}
              />
            )}
          </Row>
        </InputGroup>
      ))}
      <TextArea
        label={`${t('dats.inputText', 'Input Text')} (The input file should not have CSV headers.)`}
        value={createJobState.inputs.inputText}
        onChange={(event) => createJobActions.setInputText(event.target.value)}
        placeholder={t(`dats.enterInputHereOrSelectAInputFile`, `Enter Input Here or Select an Input File`)}
        disabled={createJobState.disableInputTextbox}
      />
      {createJobState.uploadProgress && (
        <div>Uploading file: {Math.round((createJobState.uploadProgress || 0) * 100)}%</div>
      )}
      <input
        data-testid="uploadFileButton"
        type="file"
        name="file"
        onChange={(event) => createJobActions.setInputFile(event.target.files[0])}
        disabled={createJobState.disableInputFile}
      />
    </div>
  )
}
